/* eslint-disable @typescript-eslint/no-explicit-any */
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {organisationApiClient} from '#lib/api-client';
import {queryKeys} from '#lib/query-client';

// Hook for subscribing to an organisation
export function useSubscribeOrganisation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (organisationId: string | number) =>
      organisationApiClient.subscribeOrganisation(organisationId),
    onSuccess: (_, organisationId) => {
      // Only invalidate subscriptions and specific organisation detail
      queryClient.invalidateQueries({queryKey: queryKeys.subscriptions.all});
      queryClient.invalidateQueries({
        queryKey: queryKeys.organisation.detail(organisationId)
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.organisation.webshop(organisationId)
      });
      queryClient.invalidateQueries({queryKey: queryKeys.user.subscriptions()});
    }
  });
}

// Hook for unsubscribing from an organisation
export function useUnsubscribeOrganisation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (organisationId: string | number) =>
      organisationApiClient.unsubscribeOrganisation(organisationId),
    onSuccess: (_, organisationId) => {
      // Only invalidate subscriptions and specific organisation detail
      queryClient.invalidateQueries({queryKey: queryKeys.subscriptions.all});
      queryClient.invalidateQueries({
        queryKey: queryKeys.organisation.detail(organisationId)
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.organisation.webshop(organisationId)
      });
      queryClient.invalidateQueries({queryKey: queryKeys.user.subscriptions()});
    }
  });
}

export function useSubscribeMerchant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (merchantId: number) =>
      organisationApiClient.subscribeMerchant(merchantId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.organisation.all, 'merchants'],
        refetchType: 'inactive'
      });
    }
  });
}

// Hook for unsubscribing from a merchant
export function useUnsubscribeMerchant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (merchantId: number) =>
      organisationApiClient.unsubscribeMerchant(merchantId),
    onSuccess: () => {
      // Only invalidate merchants queries, not all organisation queries
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.organisation.all, 'merchants']
      });
    }
  });
}

// Hook for bulk unsubscribing from merchants
export function useBulkUnsubscribeMerchant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (merchantIds: number[]) =>
      organisationApiClient.bulkUnsubscribeMerchant(merchantIds),
    onSuccess: () => {
      // Only invalidate merchants queries, not all organisation queries
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.organisation.all, 'merchants']
      });
    }
  });
}

// Hook for retreiving org code for unsubscribed members
export function useRetreiveOrgCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (orgId: number) => organisationApiClient.retreiveOrgCode(orgId),
    onSuccess: () => {
      // Only invalidate merchants queries, not all organisation queries
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.organisation.all, 'code']
      });
    }
  });
}

// Hook for deleting organisation member
export function useDeleteOrganisationMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Record<string, any>) =>
      organisationApiClient.deleteOrganisationMember(data),
    onSuccess: () => {
      // Only invalidate members-related queries, not all organisation queries
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.organisation.all, 'members']
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.organisation.all, 'pending-members']
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.organisation.all, 'invited-members']
      });
    }
  });
}

// Hook for inviting organisation member
export function useInviteOrganisationMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Record<string, any>) =>
      organisationApiClient.inviteOrganisationMember(data),
    onSuccess: () => {
      // Only invalidate members-related queries, not all organisation queries
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.organisation.all, 'pending-members']
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.organisation.all, 'invited-members']
      });
    }
  });
}

// Hook for uploading member CSV
export function useUploadMemberCsv() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      language,
      formData
    }: {
      language: string;
      formData: FormData;
    }) => organisationApiClient.uploadMemberCsv(language, formData),
    onSuccess: () => {
      // Only invalidate members-related queries, not all organisation queries
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.organisation.all, 'pending-members']
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.organisation.all, 'invited-members']
      });
    }
  });
}

// Hook for getting member QR code
export function useGetMemberQrCode() {
  return useMutation({
    mutationFn: () => organisationApiClient.getMemberQrCode()
  });
}

// Hook for accepting merchant request
export function useAcceptMerchantRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string | number) =>
      organisationApiClient.acceptMerchantOrganisationRequest(id),
    onSuccess: () => {
      // Only invalidate merchant requests queries, not all organisation queries
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.organisation.all, 'merchant-requests']
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.organisation.all, 'merchants']
      });
    }
  });
}

// Hook for declining merchant request
export function useDeclineMerchantRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string | number) =>
      organisationApiClient.declineMerchantOrganisationRequest(id),
    onSuccess: () => {
      // Only invalidate merchant requests queries, not all organisation queries
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.organisation.all, 'merchant-requests']
      });
    }
  });
}
